import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  Rocket,
  Users,
  Globe,
  Target,
  TrendingUp,
  Sparkles,
  Star,
  DollarSign,
  Award,
  PieChart
} from "lucide-react";
import HeroImage from "../../assets/images/journey/YouthSummit_Y20.jpg";

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen overflow-hidden bg-white">
      {/* Big Container */}
      <div className="flex flex-col lg:flex-row min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 gap-8 sm:gap-12 lg:gap-16 items-center">
        {/* Left Container - Text */}
        <div className="w-full lg:w-1/2 flex items-center order-2 lg:order-1">
          <div className="w-full">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-50 to-purple-50 border border-purple-200 rounded-full"
              >
                <Rocket className="w-4 h-4 text-purple-600 mr-2" />
                <span className="text-purple-700 text-sm font-semibold">Innovation Initiative</span>
              </motion.div>

              {/* Main Title */}
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight"
                >
                  <span className="text-gray-900">Youth</span>
                  <br />
                  <span className="bg-gradient-to-r from-purple-600 to-purple-600 bg-clip-text text-transparent">Entrepreneurship</span>
                </motion.h1>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-lg"
              >
                Nurturing the next generation of entrepreneurs through mentorship, funding opportunities, and skill development programs that turn innovative ideas into impactful ventures.
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="flex flex-wrap gap-6 sm:gap-8 pt-4"
              >
                <div>
                  <div className="text-2xl font-bold text-purple-600">Innovation</div>
                  <div className="text-sm text-gray-600 font-medium">Driven</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">Startup</div>
                  <div className="text-sm text-gray-600 font-medium">Ecosystem</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">Mentorship</div>
                  <div className="text-sm text-gray-600 font-medium">Network</div>
                </div>
              </motion.div>

    
            </motion.div>
          </div>
        </div>

        {/* Right Container - Rounded Image */}
        <div className="w-full lg:w-1/2 flex items-center justify-center order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative w-full"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-purple-600 rounded-3xl opacity-20 blur-2xl"></div>
            <div className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src={HeroImage}
                alt="Youth Summit - Young entrepreneurs and innovation"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
            Our <span className="text-purple-600">Mission</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Empowering young entrepreneurs to transform innovative ideas into sustainable businesses that drive economic growth and social impact
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left - Mission Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Innovation Catalyst</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Providing young entrepreneurs with the resources, mentorship, and networks needed to transform groundbreaking ideas into successful ventures.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <PieChart className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Skill Development</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Comprehensive training programs covering business fundamentals, digital marketing, financial literacy, and leadership skills essential for startup success.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Economic Impact</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Creating job opportunities, fostering innovation ecosystems, and contributing to sustainable economic growth at local and global levels.
                  </p>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="bg-purple-50 rounded-2xl p-6 border border-purple-200"
            >
              <h4 className="font-bold text-purple-800 mb-3 flex items-center">
                <Sparkles className="w-5 h-5 mr-2" />
                Vision Statement
              </h4>
              <p className="text-purple-700 italic">
                "To build a world where every young person with an entrepreneurial spirit has access to the tools, knowledge, and support needed to create ventures that solve real problems and generate positive impact."
              </p>
            </motion.div>
          </motion.div>

          {/* Right - Mission Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={HeroImage}
                alt="Young entrepreneurs collaborating"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              

            </div>

            {/* Floating Element */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-6 -right-6 w-24 h-24 bg-purple-500 rounded-full flex items-center justify-center shadow-xl"
            >
              <Rocket className="w-8 h-8 text-white" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Impact Section
const ImpactSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const impactStats = [
    { icon: Rocket, label: "Startup Ecosystem", text: "Thriving" },
    { icon: DollarSign, label: "Investment Support", text: "Growing" },
    { icon: Users, label: "Employment Impact", text: "Significant" },
    { icon: Award, label: "Success Stories", text: "Inspiring" },
    { icon: Globe, label: "Global Reach", text: "Expanding" },
    { icon: Star, label: "Mentor Network", text: "Extensive" }
  ];

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-purple-50 to-purple-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
            Initiative <span className="text-purple-600">Impact</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Transforming entrepreneurial dreams into thriving businesses that create value and drive innovation
          </p>
        </motion.div>

        {/* Impact Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-20">
          {impactStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-purple-200/50"
            >
              <div className="flex items-center justify-between mb-6">
                <div className={`w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg`}>
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="w-3 h-3 bg-purple-400 rounded-full"
                />
              </div>
              
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {stat.text}
              </div>
              <p className="text-gray-700 font-semibold">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main Youth Entrepreneurship Component
const YouthEntrepreneurshipInitiative = () => {
  useEffect(() => {
    document.title = "Youth Entrepreneurship Initiative - Harsh Sanghani";
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <HeroSection />
      <MissionSection />
      <ImpactSection />
    </div>
  );
};

export default YouthEntrepreneurshipInitiative;